/**
 * Main FlowLight orchestrator class
 */

import { EventBus } from './EventBus.js';
import { ApiService } from '../services/ApiService.js';
import { FloatingButton } from '../ui/components/FloatingButton.js';
import { SearchInterface } from '../ui/components/SearchInterface.js';
import { DEFAULT_OPTIONS } from '../constants/Defaults.js';
import { MESSAGES } from '../constants/Messages.js';
import { KeyboardUtils } from '../utils/KeyboardUtils.js';

export class FlowLight {
  constructor(options = {}) {
    // Merge options with defaults
    this.options = {
      ...DEFAULT_OPTIONS,
      ...options
    };

    // Initialize core services
    this.eventBus = new EventBus();
    this.apiService = new ApiService(this.options);

    // Initialize components
    this.components = {
      button: new FloatingButton(this.options, this.eventBus),
      search: new SearchInterface(this.options, this.eventBus)
    };

    // State management
    this.isInitialized = false;
    this.isSearchVisible = false;

    // Initialize if DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }

    if (this.options.debug) {
      console.log(MESSAGES.flowlightInitialized, this.options);
    }
  }

  /**
   * Initialize FlowLight
   */
  init() {
    if (this.isInitialized) return;

    try {
      // Render components
      this.components.button.render();
      this.components.search.create();

      // Bind events
      this.bindEvents();

      // Bind keyboard shortcuts
      this.bindKeyboardShortcuts();

      this.isInitialized = true;

      if (this.options.debug) {
        console.log('FlowLight initialized successfully');
      }
    } catch (error) {
      console.error('Failed to initialize FlowLight:', error);
    }
  }

  /**
   * Bind component events
   */
  bindEvents() {
    // Button click event
    this.eventBus.on('button:click', () => {
      this.toggleSearch();
    });

    // Search toggle event (for external components)
    this.eventBus.on('search:toggle', () => {
      this.toggleSearch();
    });
  }

  /**
   * Bind keyboard shortcuts
   */
  bindKeyboardShortcuts() {
    // Only bind keyboard shortcuts if enabled
    if (!this.options.keyboardShortcuts) {
      if (this.options.debug) {
        console.log('Keyboard shortcuts disabled in options');
      }
      return;
    }

    // Clean up existing listener if any
    if (this.keyboardCleanup) {
      this.keyboardCleanup();
      this.keyboardCleanup = null;
    }

    const cleanup = KeyboardUtils.addGlobalKeyboardListener('keydown', (event) => {
      if (this.options.debug) {
        console.log('🔍 Keyboard event detected:', {
          key: event.key,
          ctrlKey: event.ctrlKey,
          metaKey: event.metaKey,
          shiftKey: event.shiftKey,
          altKey: event.altKey,
          isSearchShortcut: KeyboardUtils.isSearchShortcut(event),
          isSearchVisible: this.isSearchVisible
        });
      }
      
      if (KeyboardUtils.isSearchShortcut(event)) {
        KeyboardUtils.preventDefault(event);
        if (this.options.debug) {
          console.log('⌨️ Search shortcut triggered!');
        }
        this.toggleSearch();
      }
    });

    // Store cleanup function for later disposal
    this.keyboardCleanup = cleanup;
    
    if (this.options.debug) {
      console.log('✅ Keyboard shortcuts bound successfully');
    }
  }

  /**
   * Refresh keyboard shortcuts (useful for debugging)
   */
  refreshKeyboardShortcuts() {
    if (this.options.debug) {
      console.log('🔄 Refreshing keyboard shortcuts...');
    }
    
    this.disableKeyboardShortcuts();
    this.enableKeyboardShortcuts();
    
    if (this.options.debug) {
      console.log('✅ Keyboard shortcuts refreshed');
    }
  }

  /**
   * Toggle search visibility
   */
  toggleSearch() {
    if (this.options.debug) {
      console.log('🔄 Toggle search called:', {
        isSearchVisible: this.isSearchVisible,
        searchInterfaceVisible: this.components.search.isVisible
      });
    }
    
    if (this.isSearchVisible) {
      this.hideSearch();
    } else {
      this.showSearch();
    }
  }

  /**
   * Show search interface
   */
  showSearch() {
    this.isSearchVisible = true;
    this.components.search.show();
    this.eventBus.emit('search:shown');
    
    if (this.options.debug) {
      console.log(MESSAGES.searchShown);
    }
  }

  /**
   * Hide search interface
   */
  hideSearch() {
    this.isSearchVisible = false;
    this.components.search.hide();
    this.eventBus.emit('search:hidden');
    
    if (this.options.debug) {
      console.log(MESSAGES.searchHidden);
    }
  }

  /**
   * Get current search state
   * @returns {Object} - Search state
   */
  getSearchState() {
    return {
      isVisible: this.isSearchVisible
    };
  }

  /**
   * Update FlowLight options
   * @param {Object} newOptions - New options to merge
   */
  updateOptions(newOptions) {
    this.options = {
      ...this.options,
      ...newOptions
    };

    // Update components with new options
    if (this.components.button) {
      this.components.button.updateSize(this.options.buttonSize);
      this.components.button.updatePosition(this.options.buttonPosition);
    }

    // Handle keyboard shortcuts option change
    if (newOptions.hasOwnProperty('keyboardShortcuts')) {
      if (newOptions.keyboardShortcuts) {
        this.enableKeyboardShortcuts();
      } else {
        this.disableKeyboardShortcuts();
      }
    }

    if (this.options.debug) {
      console.log('FlowLight options updated:', this.options);
    }
  }

  /**
   * Enable keyboard shortcuts
   */
  enableKeyboardShortcuts() {
    if (this.keyboardCleanup) {
      // Already enabled
      return;
    }
    
    this.bindKeyboardShortcuts();
    
    if (this.options.debug) {
      console.log('Keyboard shortcuts enabled');
    }
  }

  /**
   * Disable keyboard shortcuts
   */
  disableKeyboardShortcuts() {
    if (this.keyboardCleanup) {
      this.keyboardCleanup();
      this.keyboardCleanup = null;
      
      if (this.options.debug) {
        console.log('Keyboard shortcuts disabled');
      }
    }
  }

  /**
   * Check if keyboard shortcuts are enabled
   * @returns {boolean} - True if keyboard shortcuts are enabled
   */
  areKeyboardShortcutsEnabled() {
    return this.options.keyboardShortcuts && this.keyboardCleanup !== null;
  }

  /**
   * Get keyboard shortcut info
   * @returns {Object} - Keyboard shortcut information
   */
  getKeyboardShortcutInfo() {
    return {
      enabled: this.areKeyboardShortcutsEnabled(),
      shortcut: 'Cmd+K (Mac) or Ctrl+K (Windows/Linux)',
      description: 'Toggle search interface'
    };
  }

  /**
   * Get FlowLight instance
   * @returns {FlowLight} - Current instance
   */
  getInstance() {
    return this;
  }

  /**
   * Highlight an element with overlay
   * @param {HTMLElement} element - Element to highlight
   * @param {Object} options - Highlight options
   * @returns {HTMLElement|null} - Highlight overlay element
   */
  highlightElement(element, options = {}) {
    return this.components.search.highlightElement(element, options);
  }

  /**
   * Remove highlight overlay
   */
  removeHighlight() {
    this.components.search.removeHighlight();
  }

  /**
   * Set custom search placeholder
   * @param {string} placeholder - Custom placeholder text
   */
  setSearchPlaceholder(placeholder) {
    this.components.search.setSearchPlaceholder(placeholder);
  }

  /**
   * Update search placeholder based on input width
   */
  updateSearchPlaceholder() {
    this.components.search.updateSearchPlaceholder();
  }

  /**
   * Get detailed state information for debugging
   * @returns {Object} - Detailed state information
   */
  getDebugState() {
    return {
      isSearchVisible: this.isSearchVisible,
      searchInterfaceVisible: this.components.search.isVisible,
      keyboardShortcutsEnabled: this.areKeyboardShortcutsEnabled(),
      keyboardCleanupExists: this.keyboardCleanup !== null,
      options: {
        keyboardShortcuts: this.options.keyboardShortcuts,
        debug: this.options.debug
      }
    };
  }

  /**
   * Destroy FlowLight instance
   */
  destroy() {
    try {
      // Clean up components
      Object.values(this.components).forEach(component => {
        if (component && typeof component.destroy === 'function') {
          component.destroy();
        }
      });

      // Clean up event bus
      this.eventBus.clear();

      // Clean up keyboard listeners
      if (this.keyboardCleanup) {
        this.keyboardCleanup();
      }

      // Reset state
      this.isInitialized = false;
      this.isSearchVisible = false;
      this.components = {};

      if (this.options.debug) {
        console.log('FlowLight destroyed');
      }
    } catch (error) {
      console.error('Error destroying FlowLight:', error);
    }
  }
} 